import { CartErrorEvent, CartLinesUpdateEvent } from '@shopify/events';

class KtMultiKitConfigurator extends HTMLElement {
  connectedCallback() {
    this.variantData = JSON.parse(this.querySelector('[data-kt-multi-kit-variants]')?.textContent || '[]');
    this.mode = this.dataset.mode === 'chest' ? 'chest' : 'complete';
    this.minimumCount = 1;
    this.configurations = [];
    this.tierInputs = [...this.querySelectorAll('.kt-multi-kit__tier input')];
    this.configurationsElement = this.querySelector('[data-kt-configurations]');
    this.addAreaButton = this.querySelector('[data-kt-add-area]');
    this.submitButton = this.querySelector('[data-kt-submit]');
    this.errorElement = this.querySelector('[data-kt-error]');
    this.liveElement = this.querySelector('[data-kt-live]');
    this.summary = {
      count: this.querySelector('[data-kt-count]'),
      discount: this.querySelector('[data-kt-discount]'),
      subtotal: this.querySelector('[data-kt-subtotal]'),
      savings: this.querySelector('[data-kt-savings]'),
      total: this.querySelector('[data-kt-total]'),
      average: this.querySelector('[data-kt-average]'),
    };

    if (!this.variantData.length || !this.configurationsElement || !this.submitButton) return;

    this.optionValues = this.#collectOptionValues();
    this.defaultOptions = [...(this.variantData.find((variant) => variant.available) || this.variantData[0]).options];
    this.#setCount(1);

    this.tierInputs.forEach((input) => input.addEventListener('change', () => this.#selectTier(Number(input.value))));
    this.addAreaButton?.addEventListener('click', () => this.#addArea());
    this.submitButton.addEventListener('click', () => this.#submit());
  }

  #collectOptionValues() {
    const count = this.mode === 'complete' ? 2 : 1;
    return Array.from({ length: count }, (_, index) => [
      ...new Set(this.variantData.map((variant) => variant.options[index]).filter(Boolean)),
    ]);
  }

  #selectTier(count) {
    this.minimumCount = count;
    this.#setCount(count);
  }

  #setCount(count) {
    while (this.configurations.length < count) this.configurations.push([...this.defaultOptions]);
    if (this.configurations.length > count) this.configurations.length = count;
    this.#render();
  }

  #addArea() {
    this.configurations.push([...this.defaultOptions]);
    this.#render();
    this.configurationsElement?.lastElementChild?.querySelector('select')?.focus();
  }

  #removeArea(index) {
    if (this.configurations.length <= this.minimumCount) return;
    this.configurations.splice(index, 1);
    this.#render();
    this.addAreaButton?.focus();
  }

  #render() {
    this.configurationsElement.replaceChildren(
      ...this.configurations.map((options, index) => this.#createConfiguration(options, index))
    );
    if (this.addAreaButton) this.addAreaButton.hidden = this.minimumCount < 4;
    this.#updateSummary();
  }

  #createConfiguration(options, index) {
    const article = document.createElement('article');
    article.className = 'kt-multi-kit__configuration';

    const head = document.createElement('div');
    head.className = 'kt-multi-kit__configuration-head';
    const title = document.createElement('h3');
    title.className = 'kt-multi-kit__configuration-title';
    title.textContent = `${this.dataset.areaLabel} ${index + 1}`;
    head.append(title);

    if (this.configurations.length > this.minimumCount) {
      const remove = document.createElement('button');
      remove.type = 'button';
      remove.className = 'kt-multi-kit__remove';
      remove.textContent = this.dataset.removeLabel;
      remove.addEventListener('click', () => this.#removeArea(index));
      head.append(remove);
    }

    const fields = document.createElement('div');
    fields.className = 'kt-multi-kit__fields';
    this.optionValues.forEach((values, optionIndex) => {
      const label = document.createElement('label');
      label.className = 'kt-multi-kit__field';
      const labelText = document.createElement('span');
      labelText.textContent = optionIndex === 0 ? this.dataset.chestLabel : this.dataset.helmetLabel;
      const select = document.createElement('select');
      select.setAttribute('aria-label', `${labelText.textContent} — ${this.dataset.areaLabel} ${index + 1}`);
      values.forEach((value) => {
        const option = document.createElement('option');
        option.value = value;
        option.textContent = value;
        option.selected = options[optionIndex] === value;
        select.append(option);
      });
      select.addEventListener('change', () => {
        this.configurations[index][optionIndex] = select.value;
        this.#updateCardAvailability(article, this.#resolveVariant(this.configurations[index]));
        this.#updateSummary();
      });
      label.append(labelText, select);
      fields.append(label);
    });

    const availability = document.createElement('p');
    availability.className = 'kt-multi-kit__availability';
    availability.dataset.ktAvailability = '';
    article.append(head, fields, availability);
    this.#updateCardAvailability(article, this.#resolveVariant(options));
    return article;
  }

  #resolveVariant(options) {
    return this.variantData.find((variant) =>
      this.optionValues.every((_, optionIndex) => variant.options[optionIndex] === options[optionIndex])
    );
  }

  #updateCardAvailability(card, variant) {
    const availability = card.querySelector('[data-kt-availability]');
    if (!availability) return;
    const available = Boolean(variant?.available);
    availability.textContent = available ? this.dataset.availableLabel : this.dataset.unavailableLabel;
    availability.classList.toggle('is-unavailable', !available);
  }

  #discountForCount(count) {
    const discountKeys = {
      1: 'discountOne',
      2: 'discountTwo',
      3: 'discountThree',
      4: 'discountFour',
    };
    const key = discountKeys[Math.min(Math.max(count, 1), 4)];
    const discount = Number(this.dataset[key]);
    return Number.isFinite(discount) ? discount : 0;
  }

  #selectedVariants() {
    return this.configurations.map((options) => this.#resolveVariant(options));
  }

  #updateSummary() {
    const variants = this.#selectedVariants();
    const count = variants.length;
    const subtotal = variants.reduce((sum, variant) => sum + Number(variant?.price || 0), 0);
    const discount = this.#discountForCount(count);
    const savings = Math.round((subtotal * discount) / 100);
    const total = subtotal - savings;
    const average = count ? Math.round(total / count) : 0;
    const ready = variants.every((variant) => variant?.available);

    this.summary.count.textContent = String(count);
    this.summary.discount.textContent = discount ? `−${discount} %` : '0 %';
    this.summary.subtotal.textContent = this.#formatMoney(subtotal);
    this.summary.savings.textContent = this.#formatMoney(savings);
    this.summary.total.textContent = this.#formatMoney(total);
    this.summary.average.textContent = this.#formatMoney(average);
    this.submitButton.textContent = count === 1
      ? this.dataset.addOneLabel
      : this.dataset.addManyLabel.replace('[count]', String(count));
    this.submitButton.disabled = !ready;
    this.errorElement.hidden = true;
  }

  #formatMoney(cents) {
    try {
      return new Intl.NumberFormat(this.dataset.locale || document.documentElement.lang || 'fr-FR', {
        style: 'currency',
        currency: this.dataset.currency || 'EUR',
      }).format(cents / 100);
    } catch {
      return `${(cents / 100).toFixed(2)} ${this.dataset.currency || 'EUR'}`;
    }
  }

  #lineProperty(index, options) {
    const parts = [`${this.dataset.areaLabel} ${index + 1}`, `${this.dataset.chestLabel} ${options[0]}`];
    if (this.mode === 'complete') parts.push(`${this.dataset.helmetLabel} ${options[1]}`);
    return parts.join(this.dataset.configSeparator || ' — ');
  }

  async #submit() {
    const variants = this.#selectedVariants();
    if (!variants.length || variants.some((variant) => !variant?.available)) {
      this.#showError(this.dataset.errorLabel);
      return;
    }

    this.submitButton.disabled = true;
    this.setAttribute('aria-busy', 'true');
    const originalLabel = this.submitButton.textContent;
    const sectionIds = [...document.querySelectorAll('cart-items-component[data-section-id]')]
      .map((element) => element.dataset.sectionId)
      .filter(Boolean);
    const items = variants.map((variant, index) => ({
      id: Number(variant.id),
      quantity: 1,
      properties: {
        [this.dataset.propertyLabel]: this.#lineProperty(index, this.configurations[index]),
      },
    }));
    const deferred = CartLinesUpdateEvent.createPromise();

    this.dispatchEvent(new CartLinesUpdateEvent({
      action: 'add',
      context: 'product',
      lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
      promise: deferred.promise,
    }));

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({
          items,
          sections: sectionIds.join(','),
          sections_url: window.location.pathname,
        }),
      });
      const result = await response.json();
      if (!response.ok || result.status) throw new Error(result.description || result.message || this.dataset.errorLabel);

      const cartResponse = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
      const cart = await cartResponse.json();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: {
          items: cart.items,
          source: 'kt-multi-kit-configurator',
          sourceId: this.dataset.productId,
          itemCount: items.length,
          productId: this.dataset.productId,
          sections: result.sections,
          didError: false,
        },
      });
      this.liveElement.textContent = this.dataset.addedLabel;
      const drawer = document.querySelector('theme-drawer#cart-drawer');
      if (typeof drawer?.open === 'function') drawer.open();
    } catch (error) {
      deferred.reject(error);
      this.dispatchEvent(new CartErrorEvent({ error: error.message || this.dataset.errorLabel, code: 'INVALID' }));
      this.#showError(error.message || this.dataset.errorLabel);
    } finally {
      this.removeAttribute('aria-busy');
      this.submitButton.disabled = false;
      this.submitButton.textContent = originalLabel;
      this.#updateSummary();
    }
  }

  #showError(message) {
    this.errorElement.textContent = message;
    this.errorElement.hidden = false;
    this.errorElement.focus?.();
  }
}

if (!customElements.get('kt-multi-kit-configurator')) {
  customElements.define('kt-multi-kit-configurator', KtMultiKitConfigurator);
}
